import {
  EventEmitterWithIndex,
  withErrorBoundary,
  getTranslatedText,
  useUserMSPCheckHook
} from '@nile/components';
import SuperEditor from '@nile/components/src/Components/RichTextEditor/SuperEditor';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import { Button, Card, TextField } from '@superops/ui-design-system';
import React, { useEffect, useState, useLayoutEffect } from 'react';
import GlobalTicketConversationUsers from '@nile/mangifera/src/Containers/ChatContainers/GlobalTicketConversationUsers';
import { SVGIcon } from '@superops/super-icons';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import BannerComponent from '@nile/mangifera/src/Components/BannerComponent';
import { useIntl } from 'react-intl';
import Actions from './Actions';
import Header from './Header';
import RecipientsConfig from './RecipientsConfig';
import { useChatResize } from './useChatResize';

let broadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

const Subject = (props) => {
  const { config } = props;
  const intl = useIntl();

  const [value, setValue] = useState(config?.value);

  const onChange = (val) => {
    setValue(val);
    if (config?.onChange) {
      config?.onChange(val);
    }
  };

  return (
    <TextField
      value={value}
      onChange={onChange}
      placeholder={getTranslatedText(intl, 'app.create.email.subject')}
      // variant="search"
      noborder
    />
  );
};

const Editor = ({
  editorConfig,
  editorRef,
  hideToolbar,
  agentCollisionConfig
}) => {
  const sendEventConfig = agentCollisionConfig?.sendEventConfig;
  const isMSP = useUserMSPCheckHook();

  const typingTimeoutRef = React.useRef(null);

  const handleChange = () => {
    if (isMSP && sendEventConfig) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      const getPayload = (event) => {
        const entity = sendEventConfig?.entity;
        const recordId = sendEventConfig?.entityId || sendEventConfig?.recordId;
        return {
          type: 'CLIENT_EVENT',
          page: {
            type: 'DETAIL',
            entity: entity,
            recordId: recordId
          },
          data: {
            allowSimultaneousPage: true
          },
          event: event,
          epochMillis: new Date().getTime().toString()
        };
      };

      const sendTypingPayload = getPayload('TYPING_CONVERSATION');
      broadcastChannel.postMessage(
        JSON.stringify({
          name: 'wsdata',
          data: { context: 'pageUpdate', payload: sendTypingPayload }
        })
      );
      typingTimeoutRef.current = setTimeout(() => {
        const sendStoppedTypingPayload = getPayload(
          'STOPPED_TYPING_CONVERSATION'
        );
        broadcastChannel.postMessage(
          JSON.stringify({
            name: 'wsdata',
            data: { context: 'pageUpdate', payload: sendStoppedTypingPayload }
          })
        );
      }, 5000);
    }
  };

  useEffect(() => {
    return function cleanup() {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="fhp w100 col flex">
      <SuperEditor
        {...editorConfig}
        editorRef={editorRef}
        hideToolbar={hideToolbar}
        focusStart
        onChange={handleChange}
      />
    </div>
  );
};

const GlobalChatBoxV2 = (props) => {
  const {
    chat,
    toggleActiveChat,
    activeChat,
    removeChat,
    updateTabProperty
  } = props;

  const intl = useIntl();

  const defaultInitialWidth = 500;

  const minWidth = 450;
  const defaultMaxWidth = 800;

  const minHeight = 600;

  const defaultHeight = 700;

  const [dynamicConstraints, setDynamicConstraints] = useState({
    initialWidth: defaultInitialWidth,
    maxHeight: defaultHeight,
    maxWidth: defaultMaxWidth
  });

  const maxHeight = dynamicConstraints?.maxHeight;
  const defaultWidth = dynamicConstraints?.initialWidth;
  const maxWidth = dynamicConstraints?.maxWidth;

  const subject = chat?.subject;
  const actions = chat?.actions;
  const data = chat?.data;
  const config = chat?.config;
  const components = chat?.components;

  const hasWorklog = config?.hasWorklog;

  const isChatActive = chat?.id === activeChat;
  const isChatActiveRef = React.useRef(null);
  isChatActiveRef.current = isChatActive;

  const editorConfig = config?.editor;

  const subjectConfig = config?.subject;

  const [hideToolbar, setHideToolbar] = useState(true);
  const editorRef = React.useRef(null);

  const ToolbarAction = components?.toolbarAction?.component;
  const toolbarActionProps = components?.toolbarAction?.props || {};

  const PrimaryAction = components?.primaryAction?.component;
  const primaryActionProps = components?.primaryAction?.props || {};

  const SecondaryAction = components?.secondaryAction?.component;
  const secondaryActionProps = components?.secondaryAction?.props || {};

  const HeaderAction = components?.headerAction?.component;
  const HeaderActionProps = components?.headerAction?.props || {};

  const recipientsConfig = config?.recipients;
  const agentCollisionConfig = config?.agentCollision;

  const toggleEditorToolbar = () => {
    const editor = editorRef.current?.FroalaEditorRef?.current;
    // While adding link it won't be appended properly and gets added as new text
    // Reached out to Froala team and added editor?.events?.focus()
    // This ensures that when the toolbar is toggled, the editor also gains focus, allowing the toolbar to behave as expected.
    editor?.events?.focus();
    setHideToolbar(!hideToolbar);
  };

  const { template, resetTemplate, updateTemplate } = useSuperEditor(
    editorConfig?.storeConfig
  );

  const { width, height, ref, handleResize } = useChatResize({
    width: defaultWidth, // initial width
    height: defaultHeight, // initial height
    minWidth: minWidth,
    maxWidth: maxWidth,
    minHeight: minHeight,
    maxHeight: maxHeight
  });

  const insertContentTimeOutRef = React.useRef(null);
  const replaceContentTimeOutRef = React.useRef(null);

  const replaceContent = (content) => {
    if (replaceContentTimeOutRef.current) {
      clearTimeout(replaceContentTimeOutRef.current);
    }
    replaceContentTimeOutRef.current = setTimeout(() => {
      EventEmitterWithIndex.dispatch('replaceEditorContent', content);
    }, 200);
  };

  const insertContent = (content) => {
    if (insertContentTimeOutRef.current) {
      clearTimeout(insertContentTimeOutRef.current);
    }
    insertContentTimeOutRef.current = setTimeout(() => {
      EventEmitterWithIndex.dispatch('insertEditorContent', content);
    }, 200);
  };

  const insertGlobalPopupTemplate = ({ template: templateData, id, type }) => {
    if (chat?.id === id) {
      const content = templateData?.content;
      const sanitizedContent = dompurifier(content);

      if (!isChatActiveRef.current) {
        toggleActiveChat(chat?.id);
      }

      const attachments =
        templateData?.attachments?.length > 0
          ? templateData?.attachments
          : null;

      if (attachments) {
        const templateAttachments = template?.attachments || [];
        const allAttachments = [...templateAttachments, ...attachments];
        updateTemplate({
          attachments: allAttachments
        });
      }
      if (type === 'replace') {
        replaceContent(sanitizedContent);
      } else {
        insertContent(sanitizedContent);
      }
    }
  };

  useLayoutEffect(() => {
    const computeConstraints = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const computeWidth = Math.floor(viewportWidth * 0.5) - 110; // 110 is added to reduce the side magin with intercomm

      const computedInitialWidth = Math.max(computeWidth, minWidth);

      const maxViewPortWidth = Math.floor(viewportWidth * 0.6);

      const computedMaxWidth = Math.max(maxViewPortWidth, defaultMaxWidth);

      const computedMaxHeight = viewportHeight - 150;

      setDynamicConstraints({
        initialWidth: computedInitialWidth,
        maxHeight: computedMaxHeight,
        maxWidth: computedMaxWidth
      });
    };

    computeConstraints();

    window.addEventListener('resize', computeConstraints);
    return () => {
      window.removeEventListener('resize', computeConstraints);
    };
  }, []);

  useEffect(() => {
    const eventIndex = EventEmitterWithIndex.subscribe(
      'insertGlobalPopupTemplate',
      insertGlobalPopupTemplate
    );
    return () => {
      EventEmitterWithIndex.unsubscribe(
        'insertGlobalPopupTemplate',
        eventIndex
      );
      if (replaceContentTimeOutRef.current) {
        clearTimeout(replaceContentTimeOutRef.current);
      }
      if (insertContentTimeOutRef.current) {
        clearTimeout(insertContentTimeOutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="position-relative z400 fhp global-chat-v2 flex col"
      id="global-chat-v2"
      ref={ref}
      style={{
        width: isChatActive ? width : defaultInitialWidth,
        height: isChatActive ? height : '70px',
        maxWidth: maxWidth,
        maxHeight: maxHeight
        // transition: 'height 0.1s ease-in-out'
      }}
    >
      <div
        className="global-chat-y-resize-handler w100 position-absolute cursor-ns-resize"
        onMouseDown={handleResize}
      />

      <div
        className="global-chat-x-resize-handler fhp position-absolute cursor-ew-resize"
        onMouseDown={handleResize}
      />

      {isChatActive && (
        <div
          className="position-absolute cursor-nwse-resize opacity4"
          id="resize-handler"
          onMouseDown={handleResize}
        >
          <SVGIcon name="ResizeNwSe" />
        </div>
      )}

      <Card AdaptHeight variant={!isChatActive && 'secondaryCard'}>
        <div className="flex fhp col" id="card-wrapper">
          <Header
            subject={subject}
            toggleActiveChat={toggleActiveChat}
            removeChat={removeChat}
            id={chat?.id}
            heading={data?.heading}
            icon={data?.icon}
            iconBg={data?.iconBg}
            onSubjectClick={actions?.onSubjectClick}
            onClose={actions?.onClose}
            isChatActive={isChatActive}
          />

          <div
            className="fhp flex col flex-1"
            id="global-chat-inside-content-wrapper no-scroll min-height-0 "
            style={{
              minHeight: '0'
            }}
          >
            {(recipientsConfig || HeaderAction) && (
              <div
                className="tw-px-4 tw-py-2 divider max-height-240 scroll"
                id="header-actions-wrapper"
              >
                {HeaderAction && (
                  <div id="global-chat-custom-header-actions">
                    <HeaderAction
                      data={chat}
                      editorRef={editorRef}
                      updateTabProperty={updateTabProperty}
                      {...HeaderActionProps}
                    />
                  </div>
                )}

                {recipientsConfig && <RecipientsConfig {...recipientsConfig} />}
              </div>
            )}

            {config?.subject && (
              <div className="tw-px-4 tw-pt-4">
                <Subject config={subjectConfig} />
              </div>
            )}

            <div
              className="tw-p-4 flex col flex-1 no-scroll tw-gap-y-4"
              id="global-chat-content-wrapper"
            >
              <div className="flex no-scroll flex-1 fhp" id="editor-wrapper">
                <Editor
                  editorConfig={editorConfig}
                  editorRef={editorRef}
                  hideToolbar={hideToolbar}
                  agentCollisionConfig={agentCollisionConfig}
                />
              </div>

              <div id="actions-agent-wrapper">
                {hasWorklog && (
                  <BannerComponent
                    bannerKey="globalReplyBanner"
                    defaultMessage={getTranslatedText(
                      intl,
                      'ticket.reply.convert.worklog.info'
                    )}
                  />
                )}
                <div
                  className="flex actr w100 fx-sb tw-gap-x-2"
                  id="global-chat-actions"
                >
                  <div>
                    <Button onClick={toggleEditorToolbar}>A</Button>
                  </div>

                  {ToolbarAction && (
                    <div
                      className="w100"
                      id="global-chat-custom-toolbar-actions"
                    >
                      <ToolbarAction
                        data={chat}
                        editorRef={editorRef}
                        updateTabProperty={updateTabProperty}
                      />
                    </div>
                  )}

                  <div id="global-chat-actions">
                    <Actions
                      data={chat}
                      primaryActionComponent={PrimaryAction}
                      primaryActionProps={primaryActionProps}
                      secondaryActionComponent={SecondaryAction}
                      secondaryActionProps={secondaryActionProps}
                      primaryAction={actions?.primaryAction}
                      defaultSecondaryAction={resetTemplate} // Called on cancel
                      template={template}
                      updateTabProperty={updateTabProperty}
                    />
                  </div>
                </div>
                {agentCollisionConfig && (
                  <div className="w100 tw-mt-2" id="agent-collision-wrapper">
                    <GlobalTicketConversationUsers
                      {...agentCollisionConfig}
                      fullWidth
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default withErrorBoundary(GlobalChatBoxV2);
